import { useEffect, useState } from 'react';
import { Search, Download, RotateCcw, Info } from 'lucide-react';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { Button } from '@/shared/components/ui/Button';
import { Tooltip } from '@/shared/components/ui/Tooltip';
import { useDebounce } from '@/shared/hooks/useDebounce';
import { useUserOptions } from '@/shared/hooks/useUserOptions';
import { STATUSES, STATUS_LABELS } from '../constants';
import type { LeadsFilters } from '../types';

interface LeadsFilterBarProps {
  filters: LeadsFilters;
  onFilterChange: (filters: Partial<LeadsFilters>) => void;
  onReset: () => void;
  onExport: () => void;
  isAdmin: boolean;
}

export function LeadsFilterBar({ filters, onFilterChange, onReset, onExport, isAdmin }: LeadsFilterBarProps) {
  const [localSearch, setLocalSearch] = useState(filters.search || '');
  const debouncedSearch = useDebounce(localSearch, 400);

  // Push debounced search into the query (resets pagination).
  useEffect(() => {
    if (debouncedSearch !== (filters.search || '')) {
      onFilterChange({ search: debouncedSearch || undefined, offset: 0 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  // Keep local field in sync when filters are reset externally.
  useEffect(() => {
    setLocalSearch(filters.search || '');
  }, [filters.search]);

  const { data: users = [] } = useUserOptions(isAdmin);
  // The backend lead `userId` filter uses parseInt(), so it only works with
  // numeric (legacy) IDs — not Mongo ObjectIds. Detect support at runtime.
  const employeeFilterSupported = users.length > 0 && users.every((u) => /^\d+$/.test(u.id));

  const statusOptions = [
    { value: '', label: 'All Statuses' },
    ...STATUSES.map((s) => ({ value: s, label: STATUS_LABELS[s] })),
  ];

  return (
    <div className="mt-4 flex flex-col gap-3 rounded-card border border-outline bg-surface p-4 shadow-surface-1">
      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-[240px] flex-1">
          <Input
            leftIcon={<Search className="h-4 w-4" />}
            placeholder="Search leads by name, email, phone…"
            value={localSearch}
            onChange={(e) => setLocalSearch(e.target.value)}
            aria-label="Search leads"
          />
        </div>
        <Button variant="ghost" size="sm" onClick={onReset}>
          <RotateCcw className="h-4 w-4" /> Reset
        </Button>
        <Button variant="outline" size="sm" onClick={onExport}>
          <Download className="h-4 w-4" /> Export CSV
        </Button>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="w-36">
          <Input
            placeholder="Niche"
            value={filters.niche || ''}
            onChange={(e) => onFilterChange({ niche: e.target.value || undefined, offset: 0 })}
            aria-label="Filter by niche"
          />
        </div>
        <div className="w-36">
          <Input
            placeholder="Location"
            value={filters.location || ''}
            onChange={(e) => onFilterChange({ location: e.target.value || undefined, offset: 0 })}
            aria-label="Filter by location"
          />
        </div>
        <div className="w-40">
          <Select
            options={statusOptions}
            value={filters.status || ''}
            onChange={(e) => onFilterChange({ status: e.target.value || undefined, offset: 0 })}
            aria-label="Filter by status"
          />
        </div>
        <div className="w-40">
          <Input
            type="date"
            value={filters.from || ''}
            onChange={(e) => onFilterChange({ from: e.target.value || undefined, offset: 0 })}
            aria-label="Created from"
          />
        </div>
        <div className="w-40">
          <Input
            type="date"
            value={filters.to || ''}
            onChange={(e) => onFilterChange({ to: e.target.value || undefined, offset: 0 })}
            aria-label="Created to"
          />
        </div>

        {isAdmin &&
          (employeeFilterSupported ? (
            <div className="w-52">
              <Select
                options={[
                  { value: '', label: 'All Employees' },
                  ...users.map((u) => ({ value: u.id, label: u.display_name })),
                ]}
                value={filters.userId || ''}
                onChange={(e) => onFilterChange({ userId: e.target.value || undefined, offset: 0 })}
                aria-label="Filter by employee"
              />
            </div>
          ) : (
            <div className="flex w-52 items-center gap-1.5">
              <Select options={[{ value: '', label: 'Employee filter unavailable' }]} disabled aria-label="Employee filter unavailable" />
              <Tooltip content="Employee filtering is unavailable on this backend: lead ownership uses non-numeric IDs the server's numeric filter cannot match.">
                <Info className="h-4 w-4 text-on-surface-variant" />
              </Tooltip>
            </div>
          ))}
      </div>
    </div>
  );
}
